import { FormEvent, useCallback, useRef, useState } from 'react';
import { queryBlocksAi } from '@/lib/blocks-ai';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export default function AiTestPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);
    scrollToBottom();

    abortRef.current = new AbortController();

    const assistantIndex = messages.length + 1; // index after the user message we just pushed
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      await queryBlocksAi({
        query: trimmed,
        basePrompt: 'You are a helpful assistant. Answer clearly and concisely.',
        signal: abortRef.current.signal,
        onMessage(chunk) {
          setMessages((prev) => {
            const updated = [...prev];
            updated[assistantIndex] = {
              ...updated[assistantIndex],
              content: updated[assistantIndex].content + chunk,
            };
            return updated;
          });
          scrollToBottom();
        },
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const errorMessage = err instanceof Error ? err.message : 'Something went wrong';
      setMessages((prev) => {
        const updated = [...prev];
        const current = updated[assistantIndex]?.content ?? '';
        updated[assistantIndex] = {
          role: 'assistant',
          content: current ? `${current}\n\nError: ${errorMessage}` : `Error: ${errorMessage}`,
        };
        return updated;
      });
    } finally {
      setLoading(false);
      abortRef.current = null;
      scrollToBottom();
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
    setLoading(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        maxWidth: 720,
        margin: '0 auto',
        padding: 16,
      }}
    >
      <h2 style={{ marginBottom: 12 }}>Selise Blocks AI Chat</h2>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid #ddd',
          borderRadius: 8,
          padding: 16,
          marginBottom: 12,
          background: '#fafafa',
        }}
      >
        {messages.length === 0 && (
          <p style={{ color: '#999', textAlign: 'center', marginTop: 40 }}>
            Send a message to start chatting
          </p>
        )}
        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                maxWidth: '75%',
                padding: '10px 14px',
                borderRadius: 12,
                background: msg.role === 'user' ? '#0066ff' : '#e9e9e9',
                color: msg.role === 'user' ? '#fff' : '#222',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                lineHeight: 1.5,
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}
        {loading && messages[messages.length - 1]?.content === '' && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                background: '#e9e9e9',
                color: '#888',
              }}
            >
              Thinking...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type a message..."
          disabled={loading}
          style={{
            flex: 1,
            padding: '10px 14px',
            borderRadius: 8,
            border: '1px solid #ccc',
            fontSize: 14,
            outline: 'none',
          }}
        />
        {loading ? (
          <button
            type="button"
            onClick={handleStop}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#dc3545',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Stop
          </button>
        ) : (
          <button
            type="submit"
            disabled={!input.trim()}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#0066ff',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 14,
              opacity: input.trim() ? 1 : 0.5,
            }}
          >
            Send
          </button>
        )}
      </form>
    </div>
  );
}
